import { Rx, SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { shopEntryId } from "../../core/primitives/workspace";
import { CustomerQrController } from "../../sections/customer-qr/customer-qr-controller";

export class ShopQrState {
  /** The customer served, carried for the tabs' links. */
  constructor(readonly customerId: string | null = null) {}
  /** The selected shop's name, which the QR opens. */
  readonly shopName = new Rx<string | null>(null);
  /** Open carts at this counter, for the Carts tab's count. */
  readonly openCarts = new Rx(0);
}

export type ShopQrDeps = Pick<AppDeps, "client" | "authHandler" | "storeRepository" | "pendingCartsRepository">;

/** The Shop QR tab: the counter's customer link as a QR code, read again when the counter changes. */
export class ShopQrController extends SubtreeController {
  readonly state: ShopQrState;
  readonly qr: CustomerQrController;
  #entryId: string | null = null;

  constructor(
    readonly deps: ShopQrDeps,
    customerId: string | null = null,
  ) {
    super();
    this.state = new ShopQrState(customerId);
    this.subtree.put(ShopQrState, this.state);
    this.qr = this.own(new CustomerQrController(this.subtree, deps));
    this.sync(() => {
      const { shopId, locationId, selectedStore } = deps.storeRepository;
      this.state.shopName.value = selectedStore?.name ?? null;
      const entryId = shopId === null ? null : shopEntryId(shopId, locationId);
      if (entryId === this.#entryId) return;
      this.#entryId = entryId;
      if (entryId === null) this.qr.close();
      else this.qr.open(entryId);
    }, [deps.storeRepository]);
    this.sync(() => {
      const { shopId, locationId } = deps.storeRepository;
      this.state.openCarts.value =
        shopId === null ? 0 : deps.pendingCartsRepository.countFor(shopEntryId(shopId, locationId));
    }, [deps.pendingCartsRepository, deps.storeRepository]);
  }
}
